import { observeServerTime } from "../time/serverClock";
import { recordApiResponseContext } from "../feedback/apiErrors";
import { signalAppRuntimeUnavailable, trackPendingApiRequest } from "../system/appRuntimeGate";
import { trackResponseBody } from "./trackResponseBody";
import { blocksStartup, readStartupStatus } from "../system/startupStatus";
import { APPLICATION_API_SCOPE, apiRequestSignal, isApiControlRequest, waitForApiRetry } from "./apiRequestGate";
import { HttpMethod, HttpStatus } from "./httpStatus";

/**
 * Gemeinsamer Fetch-Kern für mQorva-Produkt-Frontends.
 *
 * Auth läuft same-origin über das HttpOnly-Cookie (wird vom Browser automatisch
 * mitgeschickt — auch über den Vite-Dev-Proxy). Es gibt bewusst KEINEN
 * Authorization-Header und keinen Token: Der Kern darf keinen wieder einführen.
 *
 * Produktspezifisch bleiben: Basis-URL/Prefix und Endpoint-Funktionen (im
 * jeweiligen Produkt), die Auswertung des Versions-Headers (`onResponse`) sowie
 * das 401-Handling inkl. Login-Redirect (`onUnauthorized`).
 */
export type ApiRequestFn = (url: string, options?: RequestInit) => Promise<Response>;

export interface CreateApiRequestOptions {
  /** Wird für jede Response aufgerufen (z. B. UI-Build-Versionsprüfung). */
  onResponse?: (res: Response) => void;
  /** Wird bei HTTP 401 aufgerufen (produktspezifischer Login-Redirect). */
  onUnauthorized?: (res: Response) => void;
  /** Eine verspätete 401 darf keine zwischenzeitlich neue Sitzung verwerfen. */
  getAuthRevision?: () => unknown;
  /** Produktneutraler Startup-Status; nur damit werden 502/503/504 als Runtime-Ausfall bestätigt. */
  runtimeStatusEndpoint?: string;
  /** Nur notwendige Kontrollwege ausnehmen; Standard sind Shared-Auth-/Jobstatuswege. */
  isControlRequest?: (url: string, method: string) => boolean;
}

// Backend-Neustarts (Deploy) erzeugen 502/503/504 vom Reverse Proxy bzw.
// Netzwerkfehler. Idempotente Requests warten deshalb bis zur Erholung der
// Runtime. Schreibende Requests werden nur nach einer eindeutigen Ablehnung
// durch das Migration-Gate wiederholt, niemals bei ungewisser Verarbeitung.
const transientStatusCodes = new Set<number>([502, HttpStatus.ServiceUnavailable, 504]);
const retryDelaysMs = [1000, 2000, 4000, 8000];
const MS_PER_SECOND = 1000;
const MAX_MIGRATION_RETRY_DELAY_MS = 8000;
const DEFAULT_MIGRATION_RETRY_DELAY_MS = 3000;
const RUNTIME_PROBE_TIMEOUT_MS = 15_000;
/** Fehlerkennung der Migrationssperre im Antwortkörper des Backends und im Text des Fehlers. */
const API_SERVICE_UPDATING_ERROR = "ApiServiceUpdating";
const TEMPORARY_UNAVAILABLE_ERROR_NAME = "TemporaryApiUnavailableError";
const isIdempotentMethod = (method: string) => method === HttpMethod.Get || method === HttpMethod.Head;
const runtimeProbes = new Map<string, Promise<boolean>>();

async function isMigrationRejection(res: Response): Promise<boolean> {
  if (res.status !== HttpStatus.ServiceUnavailable || !res.headers.get("content-type")?.includes("application/json")) return false;
  try {
    const body = await res.clone().json() as { error?: unknown } | null;
    return body?.error === API_SERVICE_UPDATING_ERROR;
  } catch {
    return false;
  }
}

function migrationRetryDelay(res: Response): number {
  const retryAfter = res.headers.get("Retry-After");
  const seconds = retryAfter === null ? NaN : Number(retryAfter);
  return Number.isFinite(seconds) && seconds > 0
    ? Math.min(seconds * MS_PER_SECOND, MAX_MIGRATION_RETRY_DELAY_MS)
    : DEFAULT_MIGRATION_RETRY_DELAY_MS;
}

function probeRuntimeUnavailable(endpoint: string): Promise<boolean> {
  const activeProbe = runtimeProbes.get(endpoint);
  if (activeProbe) return activeProbe;

  const probe = fetch(endpoint, { cache: "no-store", signal: AbortSignal.timeout(RUNTIME_PROBE_TIMEOUT_MS) })
    .then(async (res) => {
      if (!res.ok) return transientStatusCodes.has(res.status);
      try {
        return blocksStartup(readStartupStatus(await res.json()));
      } catch {
        // Eine erreichbare, aber alte/abweichende Backend-Antwort bestätigt keinen
        // Runtime-Ausfall. Der ursprüngliche API-Status bleibt fachlich sichtbar.
        return false;
      }
    })
    .catch(() => true)
    .finally(() => runtimeProbes.delete(endpoint));

  runtimeProbes.set(endpoint, probe);
  return probe;
}

export class TemporaryApiUnavailableError extends Error {
  readonly status: number | null;

  constructor(status: number | null, cause?: unknown) {
    super(API_SERVICE_UPDATING_ERROR);
    this.name = TEMPORARY_UNAVAILABLE_ERROR_NAME;
    this.status = status;
    if (cause !== undefined) (this as Error & { cause?: unknown }).cause = cause;
  }
}

export const isTemporaryApiUnavailableError = (error: unknown): error is TemporaryApiUnavailableError =>
  error instanceof TemporaryApiUnavailableError ||
  (error instanceof Error && error.name === TEMPORARY_UNAVAILABLE_ERROR_NAME);

export const createApiRequest = (options: CreateApiRequestOptions = {}): ApiRequestFn => {
  const runtimeStatusEndpoint = options.runtimeStatusEndpoint ?? "/api/v1/system/startup-status";
  const execute: ApiRequestFn = async (url: string, requestOptions: RequestInit = {}): Promise<Response> => {
    const authRevision = options.getAuthRevision?.();
    const method = (requestOptions.method || "GET").toUpperCase();
    const retryable = isIdempotentMethod(method);

    let res: Response | undefined;
    for (let attempt = 0; ; attempt++) {
      try {
        requestOptions.signal?.throwIfAborted();
        res = await fetch(url, requestOptions);
        observeServerTime(res.headers);
      } catch (err) {
        if (requestOptions.signal?.aborted) throw err;
        signalAppRuntimeUnavailable("updating");
        if (retryable) {
          await waitForApiRetry(retryDelaysMs[Math.min(attempt, retryDelaysMs.length - 1)], requestOptions.signal);
          continue;
        }
        throw new TemporaryApiUnavailableError(null, err);
      }

      if (await isMigrationRejection(res)) {
        signalAppRuntimeUnavailable("updating");
        // Die Middleware hat _next nicht aufgerufen: keine fachliche Ausführung.
        // Verbrauchbare Streams lassen sich trotzdem nicht erneut senden.
        const streamingBody = typeof ReadableStream !== "undefined" && requestOptions.body instanceof ReadableStream;
        if (!streamingBody) {
          await waitForApiRetry(migrationRetryDelay(res), requestOptions.signal);
          continue;
        }
        throw new TemporaryApiUnavailableError(res.status);
      }

      if (transientStatusCodes.has(res.status)) {
        const runtimeUnavailable = await probeRuntimeUnavailable(runtimeStatusEndpoint);
        if (!runtimeUnavailable) break;

        signalAppRuntimeUnavailable("updating");
        if (retryable) {
          await waitForApiRetry(retryDelaysMs[Math.min(attempt, retryDelaysMs.length - 1)], requestOptions.signal);
          continue;
        }

        options.onResponse?.(res);
        recordApiResponseContext(res, { method, url });
        throw new TemporaryApiUnavailableError(res.status);
      }
      break;
    }

    requestOptions.signal?.throwIfAborted();
    options.onResponse?.(res);
    recordApiResponseContext(res, {
      method,
      url,
    });
    if (res.status === HttpStatus.Unauthorized && (!options.getAuthRevision || authRevision === options.getAuthRevision())) {
      options.onUnauthorized?.(res);
    }
    return res;
  };
  return async (url, requestOptions) => {
    const method = (requestOptions?.method ?? "GET").toUpperCase();
    const control = (options.isControlRequest ?? isApiControlRequest)(url, method);
    const signal = control ? requestOptions?.signal : apiRequestSignal(APPLICATION_API_SCOPE, method, requestOptions?.signal);
    const release = trackPendingApiRequest(method);
    try {
      return trackResponseBody(await execute(url, { ...requestOptions, signal }), method, signal);
    } finally {
      release();
    }
  };
};
